import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Table, Button, Tooltip, Space, Popconfirm, Select, Input } from "antd";
import { EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";

import { all as AllPatient } from "../../api/Patient";
import { all as AllCareUnit } from "../../api/CareUnit.js";

import { formatFirstname, formatLastname } from '../../utils';

const { Option } = Select;

const layoutSelect = {
  showSearch: true,
  allowClear: true,
  defaultActiveFirstOption: true,
  optionFilterProp: "children",
  style: {
    width: 400
  }
};

const DEFAULT_SORTER = {
  column: {
    dataIndex: "created_at"
  },
  columnKey: undefined,
  field: "created_at",
  order: "ascend"
};

const PatientTable = forwardRef((props, ref) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    showSizeChanger: true,
    position: ["topRight"],
  });
  const [sorter, setSorter] = useState({ ...DEFAULT_SORTER });
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [careUnits, setCareUnits] = useState([]);
  const [selectedCareUnitIds, setSelectedCareUnitIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [showSearchInfo, setShowSearchInfo] = useState(false);

  let timeoutFilter = null;

  const columns = [{
    title: "Prénom",
    sorter: true,
    dataIndex: "firstname",
    render: (data) => formatFirstname(data),
  }, {
    title: "Nom",
    sorter: true,
    dataIndex: "lastname",
    render: (data) => formatLastname(data),
  }, {
    title: "Âge",
    columnKey: "birthdate",
    sorter: true,
    render: (data) => {
      if (!data) {
        return "";
      }
      return moment().diff(moment(data.birthdate), "years");
    },
  }, {
    title: "Date de naissance",
    dataIndex: "birthdate",
    sorter: true,
    render: (data) => {
      if (!data) {
        return "";
      }
      return moment(data).format("DD/MM/YYYY");
    },
  }, {
    render: (data) => {
      let btn = [(
        <Tooltip placement="bottom" title="Editer">
          <Button
            type="primary"
            shape="circle"
            icon={<EditOutlined />}
            size="large"
            onClick={() => props.onEdit(data.id)}
          />
        </Tooltip>
      ), (
        <Tooltip placement="bottom" title="Supprimer">
          <Popconfirm placement="top" title={"Confirmez-vous la suppression du patient ?"} onConfirm={() => {props.onDelete(data.id)}} okText="Oui" cancelText="Non">
            <Button
              danger
              type="primary"
              shape="circle"
              icon={<DeleteOutlined />}
              size="large"
            />
          </Popconfirm>
        </Tooltip>
      )];

      return (<Space>{btn}</Space>)
    },
    width: "130px",
  }];

  /**
   * 
   * @param {*} pagination 
   * @param {*} sorter 
   * @returns 
   */
  const getPayload = (pagination, sorter) => {
    let payload = {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: {},
    };

    if (sorter.column === undefined) {
      sorter = { ...DEFAULT_SORTER };
    }

    let { order, column } = sorter;
    let field = (column.columnKey) ? column.columnKey : column.dataIndex;
    order = (order === "ascend") ? 1 : -1;

    // Traitement du sort de la column Age qui n'a pas de field a par, basé sur la birthdate
    // Sort de l'age est l'inverse du sort de la birthdate, + la birthdate est petite, + l'age est grand, et inversement;
    if (column.title === "Âge") {
      field = column.columnKey;
      order = (order === -1) ? 1 : -1;
    }

    if (!!field && !!order) {
      payload["sort"] = field + "," + order;
    }

    if (selectedCareUnitIds.length > 0) {
      payload["q"]["care_unit_ids"] = selectedCareUnitIds;
    }

    if (searchValue !== null) {
      payload["q"]["search"] = searchValue;
    }

    return payload;
  };

  /**
   * 
   * @param {*} pagination 
   * @param {*} filters 
   * @param {*} sorter 
   */
  const fetch = async function (pagination, filters, sorter) {
    setLoading(true);

    let payload = getPayload(pagination, sorter);
    AllPatient(payload)
      .then(data => {
        setLoading(false);

        setPatients(data.results);
        setPagination({
          ...pagination,
          total: data.total,
        });
        setSorter({ ...sorter });
      });
  };

  /**
   *
   * @param {*} search
   */
  const fetchCareUnits = async function (search) {
    let payload = {
      current: 1,
      pageSize: 10,
      q: {
        search,
      },
    };

    return AllCareUnit(payload)
      .then(data => {
        setCareUnits(data.results);
      })
      .catch(() => {});
  };

  /**
   *
   * @param {*} value
   */
  const onChangeCareUnits = function (value) {
    setSelectedCareUnitIds(value);
  };

  /**
   *
   * @param {*} e
   */
  const handleSearchValue = (e) => {
    let value = e.target.value;

    clearTimeout(timeoutFilter);

    let searchLength = value.length;
    let searchValid = (searchLength === 0 || searchLength > 2);

    timeoutFilter = setTimeout(() => {
      setShowSearchInfo(!searchValid);

      if (searchValid) {
        setSearchValue(e.target.value);
      }
    }, 500);
  };

  useEffect(() => {
    fetchCareUnits('');
    fetch(pagination, null, sorter);
  }, []);

  useEffect(() => {
    fetch(pagination, null, sorter);
  }, [selectedCareUnitIds, searchValue]);

  useImperativeHandle(ref, () => ({
    fetch: () => {
      fetch(pagination, null, sorter);
    },
  }));

  let careUnitOptions = careUnits.map((option, index) => (
    <Option key={index} value={option.id}>{option.name}</Option>
  ));

  return (
    <div>
      <Space className="mb-4" direction="horizontal" align="start">
        <Space direction="vertical">
          <label>Unité(s) de soin(s) :</label>
          <Select
            {...layoutSelect}
            mode="multiple"
            placeholder="Choisissez votre/vos unité(s)"
            onChange={onChangeCareUnits}
            notFoundContent="Rechercher une unité de soin"
          >
            {careUnitOptions}
          </Select>
        </Space>
        <Space direction="vertical">
          <label>Recherche :</label>
          <Tooltip
            title="La recherche ne s'applique qu'à partir de 3 caractères."
            visible={showSearchInfo}
          >
            <Input
              style={{ width: 400 }}
              suffix={<SearchOutlined />}
              placeholder="Tapez des caractères"
              allowClear={true}
              onChange={handleSearchValue}
            />
          </Tooltip>
        </Space>
      </Space>

      <Table
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={patients}
        pagination={pagination}
        loading={loading}
        onChange={fetch}
      />
    </div>
  );
});

export default PatientTable;
